import { getContract } from './BlockchainAPI';
import DynaStripesContractAddress from './Constants';
import buildTraitsText from './TraitsMetadata';

export interface TokenMetadata {
  /** Raw SVG markup, as stored on chain. */
  svg: string;
  /** The same SVG, encoded for use as an <img> src. */
  svgDataUri: string;
  traitsText: string;
  attributes: any[];
}

/**
 * Token art is generated from a seed fixed at mint time and the contract has
 * no burn or re-roll, so a token's metadata never changes once it exists --
 * it can be cached in the browser forever. Bump the version suffix if the
 * cached shape or the contract's metadata format ever changes.
 */
const CacheKeyPrefix = `DS_TOKEN_METADATA_v1_${DynaStripesContractAddress}_`;

function cacheKeyFor(tokenId: string): string {
  return CacheKeyPrefix + tokenId;
}

function readCachedMetadataJson(tokenId: string): string | null {
  try {
    return localStorage.getItem(cacheKeyFor(tokenId));
  } catch {
    return null;
  }
}

function clearCachedMetadata() {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key !== null && key.startsWith(CacheKeyPrefix)) {
        keys.push(key);
      }
    }
    keys.forEach((key) => localStorage.removeItem(key));
  } catch {
    // Nothing we can do if storage is unavailable.
  }
}

function writeCachedMetadataJson(tokenId: string, metadataJson: string) {
  try {
    localStorage.setItem(cacheKeyFor(tokenId), metadataJson);
  } catch {
    // Almost certainly the 5MB quota. The whole cache is disposable, so drop
    // it and keep the newest token rather than leaving a full store behind.
    console.log("[TokenMetadata] Cache full, clearing it.");
    clearCachedMetadata();
    try {
      localStorage.setItem(cacheKeyFor(tokenId), metadataJson);
    } catch {
      // Storage is unusable (private mode, disabled) -- carry on uncached.
    }
  }
}

/** Both metadata encodings the contract has used over its lifetime. */
function decodeMetadataDataUri(metadataDataUri: string): string {
  if (metadataDataUri.startsWith("data:text/plain,")) {
    return metadataDataUri.replace("data:text/plain,", "");
  }

  if (metadataDataUri.startsWith("data:application/json;base64,")) {
    const metadataJsonBase64Encoded = metadataDataUri.replace("data:application/json;base64,", "");
    const binaryString = atob(metadataJsonBase64Encoded);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return new TextDecoder().decode(bytes);
  }

  return "";
}

function buildMetadata(metadataJson: string): TokenMetadata {
  const metadataObject = JSON.parse(metadataJson);

  const svg = metadataObject.image.replace("data:image/svg+xml,", "");

  return {
    svg,
    svgDataUri: `data:image/svg+xml,${encodeURIComponent(svg)}`,
    traitsText: buildTraitsText(metadataObject),
    attributes: metadataObject.attributes,
  };
}

/**
 * Metadata for a token, from the local cache when we've seen it before.
 *
 * tokenURI is by far the most expensive call the site makes -- the contract
 * builds the whole SVG on chain and hands back ~5KB -- so a gallery of twelve
 * of them is what makes the page feel slow. Caching means it's paid once per
 * token per browser instead of on every visit.
 */
export async function fetchTokenMetadata(tokenId: string): Promise<TokenMetadata> {
  const cachedJson = readCachedMetadataJson(tokenId);
  if (cachedJson !== null) {
    try {
      return buildMetadata(cachedJson);
    } catch {
      console.log("[TokenMetadata] Cached metadata for " + tokenId + " unreadable, refetching.");
    }
  }

  const contract = await getContract();
  const metadataDataUri = await contract.tokenURI(tokenId);
  const metadataJson = decodeMetadataDataUri(metadataDataUri);

  const metadata = buildMetadata(metadataJson);

  writeCachedMetadataJson(tokenId, metadataJson);

  return metadata;
}
